import type { StreamFrame } from '../../core/bridge/types';
import type { BackendClient } from '../bridge/backend-client';
import type { PluginType } from '../../core/bridge/connect-client';

/*
 * PluginTypesManager — main-thread PROXY of the plugin-type feed in the Backend
 * Worker. Opens Plugins.Subscribe (one MessagePort), holds the present plugin
 * types + their forms/capabilities, and re-emits `change`. Drives the backend-
 * driven "Add controller" picker. Same manager-pattern shape as ControllerManager;
 * the source always emits full snapshots (plugin sets are tiny), so there are no
 * deltas to fold.
 */
export class PluginTypesManager extends EventTarget {
  private types: PluginType[] = [];
  private port?: MessagePort;

  constructor(
    private readonly backend: BackendClient,
    private readonly gatewayId: string,
  ) {
    super();
  }

  get all(): PluginType[] {
    return this.types;
  }
  /** Types that accept a manual AddController — what the "Add controller" picker shows. */
  get addable(): PluginType[] {
    return this.types.filter((t) => t.canManuallyAdd);
  }
  byId(pluginId: string): PluginType | undefined {
    return this.types.find((t) => t.pluginId === pluginId);
  }

  async start(): Promise<void> {
    this.port = await this.backend.api.subscribePluginTypes(this.gatewayId);
    this.port.onmessage = (e: MessageEvent<StreamFrame<PluginType>>) => this.onFrame(e.data);
    this.port.start();
  }

  stop(): void {
    this.port?.postMessage({ kind: 'close' });
    this.port = undefined;
  }

  private onFrame(frame: StreamFrame<PluginType>): void {
    if (frame.kind === 'snapshot') {
      this.types = frame.items;
    } else if (frame.kind === 'error') {
      this.types = [];
    } else {
      return; // no deltas — the source emits full snapshots
    }
    this.dispatchEvent(new CustomEvent('change'));
  }
}
